// P1-3 follow-up: which keystrokes belong to the scanner, and how a barcode is assembled.
//
// Extracted from AssetManagement.js after Christian's 08/10/2026 staging test reported that
// scanning behaved inconsistently. Two of the causes lived in the document keydown listener
// and are pure rules, so they are testable here rather than by eye.

// ── WHICH ELEMENT SHOULD SWALLOW A KEYSTROKE ────────────────────────────────
//
// The scanner is a document-level listener, so a keystroke aimed at a form field reaches both
// the field and the listener. P1-12 added this guard because with focus in Serial # two scans
// concatenated into one value (a real example: the serial `BLIND-AAA111BLIND-BBB222`).
//
// A text-entry element still wins, and it has to: the listener cannot suppress the keystroke
// (preventDefault here would stop anyone typing a note), so if it also buffered the characters
// the barcode would be BOTH captured and typed into the focused box -- and an unbound form's
// notes are saved with the entry, so the barcode text would end up in the asset's notes. That is
// worse than losing the scan.
//
// The real problem was never this rule; it was that focus could sit somewhere useless. A scan
// aimed at the Notes field, the Asset Tag field or the order Autocomplete was typed into that
// box and its terminating Enter did nothing at all -- no row, no message, no tone. The
// Autocomplete was the common case, because it keeps focus after you pick an order, so the first
// scan of a session could vanish. That is addressed where it belongs, in AssetManagement.js:
// the Serial # field takes focus when an order is opened and after every entry, the Autocomplete
// blurs once an order is chosen, and Notes / Asset Tag now answer Enter instead of ignoring it.
const isTextEntryTarget = (target) => {
    if (target == null) return false;
    const tag = (target.tagName || '').toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select'
        || target.isContentEditable === true;
};

/**
 * Should the document-level scan listener process this event?
 *
 * False whenever a text-entry element has focus — that element owns the keystroke, and the
 * Serial # field routes its own Enter to the same scan entry point.
 */
const shouldHandleScanKey = (target) => !isTextEntryTarget(target);

// ── ASSEMBLING THE BARCODE ──────────────────────────────────────────────────
//
// A scan is delimited by the terminating Enter the gun sends, NOT by a timing gap. The gap
// only exists to discard a stale fragment -- a stray keypress on the page that never got an
// Enter -- so that it cannot prefix the next real scan.
//
// It used to be 100 ms, which is shorter than a main-thread stall. Re-rendering the 850px
// asset grid takes longer than that on a large order, so a stall BETWEEN TWO CHARACTERS OF ONE
// BURST reset the buffer and the serial arrived truncated: a >=5-character tail was then
// classified as a different serial (so the item read as unexpected, or was refused as already
// on the order), and a shorter one produced "Barcode too short". Either way it did not collect,
// while the very same scan typed into the focused Serial # field -- where the DOM does the
// accumulating and no gap rule applies -- worked. That asymmetry is the other half of the
// report.
//
// 1500 ms is far above any render stall that isn't an outright hang, and still expires a
// fragment left over from minutes ago.
const STALE_FRAGMENT_MS = 1500;
const MIN_BARCODE_LENGTH = 5;

/**
 * Accumulate one printable keystroke.
 *
 * Returns the new buffer. `lastKeyTime` is the timestamp of the previous keystroke (0 if none).
 */
const appendScanKey = (buffer, key, now, lastKeyTime, staleAfterMs = STALE_FRAGMENT_MS) => {
    const previous = typeof buffer === 'string' ? buffer : '';
    const expired = lastKeyTime === 0 || (now - lastKeyTime) > staleAfterMs;
    return expired ? key : previous + key;
};

// Printable, single-character, no modifier combination that means a shortcut. Space is excluded
// deliberately: serialKey strips whitespace anyway, so a space can only ever be noise here.
const isScanCharacter = (event) => event != null
    && typeof event.key === 'string'
    && event.key.length === 1
    && event.key !== ' '
    && !event.ctrlKey && !event.altKey && !event.metaKey;

const isCompleteBarcode = (buffer) => typeof buffer === 'string' && buffer.length >= MIN_BARCODE_LENGTH;

// ── HOW DID THIS ENTRY ARRIVE? (P1-18) ──────────────────────────────────────
//
// The duplicate log recorded a `source` of `'gun'` or `'typed'` based on WHICH CODE PATH ran, and
// that was never the same question. `'gun'` was set only inside the document keydown listener,
// which by design runs only when focus is outside a text field -- so a barcode scanned into the
// focused Serial # field was labelled `typed`, which is what CompuCycle reported. The 08/11
// convergence fix then made it worse by parking the cursor in that field on purpose, so almost
// every gun scan would have reported `typed`.
//
// The honest question is how the CHARACTERS arrived, so that is what is measured:
//
//   * A scan gun in keyboard-wedge mode fires one keydown per character, milliseconds apart, then
//     a terminating Enter.
//   * A person types at roughly 100-300 ms per character.
//   * A PASTE fires no character keydowns at all -- the value simply appears. That makes it the
//     easiest of the three to identify, and it is why a naive "the value arrived fast, so it was a
//     gun" rule is wrong: a paste is not fast, it is instantaneous.
//
// ⚠ ASSUMPTION, worth confirming against the real hardware: the scanners run in keyboard-wedge
// mode. The existing scan buffer already depends on that entirely, so this adds no new risk -- but
// a scanner configured in a buffered/batch mode would deliver its whole string at once and look
// exactly like a paste. The recorded evidence below makes that diagnosable from field data rather
// than guesswork.
const ENTRY_METHOD = {
    SCANNED: 'scanned',
    KEYED: 'keyed',
    PASTED: 'pasted',
    // Deliberately a first-class answer. Asserting a guess is what produced the original report.
    UNKNOWN: 'unknown'
};

// A gun is typically 1-10 ms between characters; a person is an order of magnitude slower. 30 ms
// sits in the empty space between, but it IS a guess -- which is why `evidenceFor` reports the
// measured gap so the real figure can be read off CompuCycle's own devices.
const MAX_SCAN_GAP_MS = 30;
const MIN_SCAN_KEYSTROKES = 3;

const emptyEntryEvidence = () => ({
    keystrokes: 0,
    firstKeyAt: 0,
    lastKeyAt: 0,
    pasted: false
});

// Pure: returns new evidence rather than mutating, so it is testable and so a stale copy can never
// be half-updated.
const recordKeystroke = (evidence, now) => {
    const current = evidence ?? emptyEntryEvidence();
    return {
        ...current,
        keystrokes: current.keystrokes + 1,
        firstKeyAt: current.keystrokes === 0 ? now : current.firstKeyAt,
        lastKeyAt: now
    };
};

const recordPaste = (evidence) => ({ ...(evidence ?? emptyEntryEvidence()), pasted: true });

/**
 * Close off the evidence for a finished entry, adding what can only be derived at the end.
 *
 * `avgGapMs` is null when there was nothing to measure (fewer than two keystrokes), rather than 0 —
 * "no gap" and "an instantaneous gap" are different claims and only one of them is true.
 */
const evidenceFor = (evidence, value) => {
    const current = evidence ?? emptyEntryEvidence();
    const text = value == null ? '' : String(value);
    const spans = current.keystrokes - 1;

    return {
        ...current,
        valueLength: text.length,
        elapsedMs: current.keystrokes >= 2 ? current.lastKeyAt - current.firstKeyAt : null,
        avgGapMs: spans >= 1 ? (current.lastKeyAt - current.firstKeyAt) / spans : null
    };
};

/**
 * Classify a finished entry. Takes the output of `evidenceFor`.
 */
const classifyEntryMethod = (evidence, options = {}) => {
    const {
        maxScanGapMs = MAX_SCAN_GAP_MS,
        minScanKeystrokes = MIN_SCAN_KEYSTROKES
    } = options;

    if (evidence == null) {
        return ENTRY_METHOD.UNKNOWN;
    }

    // A paste event is direct evidence and outranks everything else.
    if (evidence.pasted === true) {
        return ENTRY_METHOD.PASTED;
    }

    // A value with no keystrokes behind it came from somewhere other than the keyboard: a paste
    // whose event was missed, a drag-and-drop, autofill, or a programmatic fill. Not a scan.
    if (evidence.keystrokes === 0 && (evidence.valueLength ?? 0) > 0) {
        return ENTRY_METHOD.PASTED;
    }

    // Too little to tell. One or two keystrokes is a plausible correction to an existing value, and
    // guessing from a single gap would be noise.
    if (evidence.keystrokes < minScanKeystrokes || evidence.avgGapMs == null) {
        return ENTRY_METHOD.UNKNOWN;
    }

    return evidence.avgGapMs <= maxScanGapMs ? ENTRY_METHOD.SCANNED : ENTRY_METHOD.KEYED;
};

// What the operator sees in the duplicate log. Kept next to the values so a new method cannot be
// added without a label.
const ENTRY_METHOD_LABELS = {
    [ENTRY_METHOD.SCANNED]: 'Scanned',
    [ENTRY_METHOD.KEYED]: 'Typed',
    [ENTRY_METHOD.PASTED]: 'Pasted',
    [ENTRY_METHOD.UNKNOWN]: 'Unknown'
};

const describeEntryMethod = (method) => ENTRY_METHOD_LABELS[method] ?? ENTRY_METHOD_LABELS[ENTRY_METHOD.UNKNOWN];

const exports = {
    STALE_FRAGMENT_MS,
    MIN_BARCODE_LENGTH,
    MAX_SCAN_GAP_MS,
    MIN_SCAN_KEYSTROKES,
    ENTRY_METHOD,
    ENTRY_METHOD_LABELS,
    isTextEntryTarget,
    shouldHandleScanKey,
    appendScanKey,
    isScanCharacter,
    isCompleteBarcode,
    emptyEntryEvidence,
    recordKeystroke,
    recordPaste,
    evidenceFor,
    classifyEntryMethod,
    describeEntryMethod,
};

export default exports;
export {
    STALE_FRAGMENT_MS,
    MIN_BARCODE_LENGTH,
    MAX_SCAN_GAP_MS,
    MIN_SCAN_KEYSTROKES,
    ENTRY_METHOD,
    ENTRY_METHOD_LABELS,
    isTextEntryTarget,
    shouldHandleScanKey,
    appendScanKey,
    isScanCharacter,
    isCompleteBarcode,
    emptyEntryEvidence,
    recordKeystroke,
    recordPaste,
    evidenceFor,
    classifyEntryMethod,
    describeEntryMethod,
};
